import { UtilitiesService } from './../../services/utilities.service';
import { Profile } from 'src/app/classes/profile';
import { PickDropEnum, TerminalRunTypeEnum, LocationEnum } from './../../classes/terminal-task';
import { TerminalRunsService } from './../../services/terminal-runs.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import moment, { Moment } from 'moment';
import { ProfileService } from 'src/app/services/profile.service';
import { TerminalNewTask } from 'src/app/classes/terminal-new-runs';
import { EventsService } from 'src/app/services/events.service';
import { EventsDeltaService } from 'src/app/services/events-delta.service';
import { asapScheduler, merge, scheduled, Subscription, timer } from 'rxjs';
import { Events } from 'src/app/classes/events';
import { TerminalRunLocation } from 'src/app/classes/terminal-run-location';
import { DriverTask } from 'src/app/classes/driver-task';
import { TaskType } from 'src/app/classes/task-type-enum';
import { switchMap, filter, map, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

const BASE_LOCATIONS_ARRAY: TerminalRunLocation[] = [
  { location: LocationEnum.ASC, passengerCount: '0' },
  { location: LocationEnum.FTC, passengerCount: '0' },
  { location: LocationEnum.Edgewood, passengerCount: '0' },
  { location: LocationEnum.Terminal, passengerCount: '0' }
];
@Component({
  standalone: false,
  selector: 'app-terminal-runs',
  templateUrl: './terminal-runs.component.html',
  styleUrls: [ './terminal-runs.component.scss' ]
})
export class TerminalRunsComponent implements OnInit, OnDestroy {
  terminalRunTasks: DriverTask[] = [];
  loading = true;
  minFilterDate = moment().toISOString();

  PICK_DROP: typeof PickDropEnum = PickDropEnum;

  subs = new Subscription();

  constructor(
    public terminalService: TerminalRunsService,
    public profileService: ProfileService,
    private utilityService: UtilitiesService,
    readonly eventsService: EventsService,
    readonly eventsDeltaService: EventsDeltaService
  ) {}

  ngOnInit(): void {
    this.subs.add(this.subToEventsAndDeltas());
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  subToEventsAndDeltas(): Subscription {
    return merge(this.eventsService.getEventData(), this.eventsDeltaService.getEventData())
      .pipe(filter((event: Events) => !!event))
      .subscribe((events: Events) => {
        this.removeAssignedTerminalTasks(events.driverTasks);
        this.overwriteAppendUnassignedTerminalTasks(events.unassignedTasks);
        this.loading = false;
      });
  }

  refreshData(): void {
    this.loading = true;
    this.eventsService.triggerEventsUpdate();
  }

  private overwriteAppendUnassignedTerminalTasks(unassignedTasks: DriverTask[]): void {
    (unassignedTasks || [])
      .filter((task: DriverTask) => task.typeName === TaskType.Terminal)
      .forEach((task: DriverTask) => {
        const currTask: DriverTask = this.terminalRunTasks.find(
          (existingTask: DriverTask) => existingTask.taskName === task.taskName
        );
        if (!currTask) {
          this.terminalRunTasks.push(task);
        } else {
          Object.keys(task).forEach((key: string) => (currTask[key] = task[key]));
        }
      });
  }

  private removeAssignedTerminalTasks(assignedTasks: DriverTask[]): void {
    (assignedTasks || [])
      .filter((task: DriverTask) => task.typeName === TaskType.Terminal)
      .forEach((task: DriverTask) => {
        const existingIndex: number = this.terminalRunTasks.findIndex(
          (existingTask: DriverTask) => existingTask.taskName === task.taskName
        );
        if (existingIndex > -1) {
          this.terminalRunTasks.splice(existingIndex, 1);
        }
      });
  }

  calcTerminalRunInitialDateTime(): string {
    const latestTime: string =
      (this.terminalRunTasks.length &&
        this.terminalRunTasks
          .map((task: DriverTask) => task.plannedEnd)
          .reduce(
            (latest: string, taskPlannedEnd: string) =>
              !latest || moment(taskPlannedEnd).isAfter(moment(latest)) ? taskPlannedEnd : latest
          )) ||
      moment().toISOString();

    const midHour: Moment = moment(latestTime).startOf('hour').add(30, 'minutes');

    return (moment(latestTime).isBefore(midHour) ? midHour : midHour.clone().add(30, 'minutes')).toISOString();
  }

  addNewTerminalTask(): void {
    this.profileService
      .getLatestProfileOnce()
      .pipe(
        switchMap((profile: Profile) => {
          const initialDateTime: Moment = moment(this.calcTerminalRunInitialDateTime());
          const newTask: TerminalNewTask = {
            taskId: `Terminal-${this.utilityService.guid()}`,
            taskTypeName: TaskType.Terminal,
            runTime: initialDateTime.format('HH:mm'),
            runDate: initialDateTime.format('MM/DD'),
            userid: profile.adId,
            pickupLocations: BASE_LOCATIONS_ARRAY,
            dropoffLocations: BASE_LOCATIONS_ARRAY,
            dropoffNotes: {
              driverNotesDropoff: '',
              coordNotesDropoff: ''
            },
            pickupNotes: {
              driverNotesPickup: '',
              coordNotesPickup: ''
            }
          };
          return this.terminalService.buildTask(newTask);
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return scheduled([], asapScheduler);
        })
      )
      .subscribe(() => this.refreshData());
  }

  updateTaskDateTime(task: DriverTask, dateTime: string): void {
    if (task.plannedEnd !== dateTime) {
      this.profileService
        .getLatestProfileOnce()
        .pipe(
          switchMap((profile: Profile) => {
            const currDateTime: Moment = moment(task.plannedEnd);
            const newDateTime: Moment = moment(dateTime);

            const hasSameTime: boolean = currDateTime.format('HH:mm') === newDateTime.format('HH:mm');

            return this.terminalService.terminalTask({
              dueDate: hasSameTime ? newDateTime.format('MM/DD') : '',
              taskName: task.taskName,
              time: hasSameTime ? '' : newDateTime.format('HH:mm'),
              type: TerminalRunTypeEnum.terminalupdate,
              updatedBy: profile.adId
            });
          })
        )
        .subscribe(() => this.refreshData());
    }
  }
}
